// src/features/blogs/admin/BlogsAdminList.jsx
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useGetAdminBlogsQuery, useDeleteBlogMutation } from '../blogsApiSlice';

function ConfirmDialog({ open, title = 'Confirm', message, confirmText = 'Delete', cancelText = 'Cancel', onConfirm, onCancel, loading = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      {/* dialog */}
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {message && <p className="mt-2 text-gray-600">{message}</p>}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="rounded-xl border px-4 py-2 text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="rounded-xl bg-red-600 px-4 py-2 text-white hover:opacity-90 disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Deleting…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BlogsAdminList() {
  const [page, setPage] = useState(1);
  const limit = 20;
  
  // Query and mutation hooks
  const { data, isLoading, isError, error, isFetching } = useGetAdminBlogsQuery(
    { page, limit },
    {
      onSuccess: (data) => console.log('Query success:', data),
      onError: (error) => console.log('Query error:', error)
    }
  );
  const [deleteBlogMutation, { isLoading: isDeleting }] = useDeleteBlogMutation();

  // Get blogs from the transformed response
  const blogs = data?.docs || [];
  const hasNext = data?.hasNextPage;
  const hasPrev = data?.hasPrevPage;
  const currentPage = data?.page ?? page;

  // Modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState('');

  const requestDelete = (id, title) => {
    setSelectedId(id);
    setSelectedTitle(title ?? '');
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedId) return;
    try {
      await deleteBlogMutation(selectedId).unwrap();
    } catch (err) {
      console.error(err);
      alert(err?.data?.message ?? 'Failed to delete blog');
    } finally {
      setConfirmOpen(false);
      setSelectedId(null);
      setSelectedTitle('');
    }
  };

  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Blogs (Admin)</h1>
          <p className="text-gray-600">Create, publish, and manage posts.</p>
        </div>
        <Link to="/dash/blogs/new" className="rounded-xl bg-black px-4 py-2 text-white hover:opacity-90">
          + New Blog
        </Link>
      </div>

      {isLoading ? (
        <div className="text-gray-500">Loading…</div>
      ) : isError ? (
        <div className="text-red-600">Error: {error?.data?.message ?? 'Failed to load blogs'}</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Slug</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Public</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Published</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map((blog) => (
                  <tr key={blog._id} className="border-t">
                    <td className="px-4 py-3">{blog.title}</td>
                    <td className="px-4 py-3 text-gray-600">{blog.slug}</td>
                    <td className="px-4 py-3">{blog.isPublic ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3">{blog.isPublished ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/dash/blogs/${blog._id}`}
                          className="rounded-lg border px-3 py-1.5 text-gray-700 hover:bg-gray-50"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => requestDelete(blog._id, blog.title)}
                          className="rounded-lg border px-3 py-1.5 text-red-600 hover:bg-red-50"
                          disabled={isDeleting}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {blogs.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-gray-500" colSpan={5}>
                      No blogs yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button
              className="rounded-xl border bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!hasPrev || isFetching}
            >
              ← Previous
            </button>
            <div className="text-sm text-gray-500">
              Page {currentPage}
              {isFetching && <span className="ml-2 animate-pulse">Refreshing…</span>}
            </div>
            <button
              className="rounded-xl border bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNext || isFetching}
            >
              Next →
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete blog"
        message={
          selectedTitle
            ? `Are you sure you want to delete "${selectedTitle}"? This action cannot be undone.`
            : 'Are you sure you want to delete this blog? This action cannot be undone.'
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setSelectedId(null); setSelectedTitle(''); }}
        loading={isDeleting}
      />
    </section>
  );
}
